/**
 * Power Histogram Chart Component
 * Visualizes power distribution with frequency bars
 * Based on IECON2023 Paper Figure 9 (left side)
 */

import React, { useRef, useEffect, useCallback, useState } from 'react';
import { Box, Typography, Modal, IconButton, useTheme } from '@mui/material';
import { Close as CloseIcon, ZoomIn as ZoomInIcon } from '@mui/icons-material';

interface PowerHistogramProps {
  histogramData: {
    counts: number[];
    bin_edges: number[];
    bin_centers: number[];
  };
  statistics?: {
    upper_bound?: number;
    Q3?: number;
  };
  title?: string;
  height?: number;
}

const PowerHistogram: React.FC<PowerHistogramProps> = ({
  histogramData,
  statistics,
  title = 'Power Distribution Histogram',
  height = 400
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [isZoomed, setIsZoomed] = useState(false);
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  // Dynamic colors based on theme
  const colors = {
    background: isDark ? '#0d1117' : '#ffffff',
    text: isDark ? 'rgba(255, 255, 255, 0.9)' : 'rgba(0, 0, 0, 0.87)',
    grid: isDark ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)',
    primary: isDark ? '#4a9eff' : '#2563eb',
    primaryLight: isDark ? 'rgba(74, 158, 255, 0.5)' : 'rgba(37, 99, 235, 0.6)',
    accent: isDark ? '#ffa726' : '#f97316',
    accentLight: isDark ? 'rgba(255, 167, 38, 0.5)' : 'rgba(249, 115, 22, 0.6)'
  };

  const drawChart = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas || !histogramData || !histogramData.counts || histogramData.counts.length === 0) {
      return;
    }

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const { counts, bin_edges, bin_centers } = histogramData;

    // Chart dimensions
    const padding = 80;
    const chartWidth = canvas.width - padding * 2;
    const chartHeight = canvas.height - padding * 2;

    // Data ranges
    const maxCount = Math.max(...counts);
    const minPower = bin_edges[0];
    const maxPower = bin_edges[bin_edges.length - 1];

    // Clear canvas
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.fillStyle = colors.background;
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    // Helper functions
    const xScale = (power: number) => padding + ((power - minPower) / (maxPower - minPower)) * chartWidth;
    const yScale = (count: number) => padding + chartHeight - (count / maxCount) * chartHeight;

    // Draw axes
    ctx.strokeStyle = colors.text;
    ctx.lineWidth = 2;

    // Y-axis
    ctx.beginPath();
    ctx.moveTo(padding, padding);
    ctx.lineTo(padding, padding + chartHeight);
    ctx.stroke();

    // X-axis
    ctx.beginPath();
    ctx.moveTo(padding, padding + chartHeight);
    ctx.lineTo(padding + chartWidth, padding + chartHeight);
    ctx.stroke();

    // Draw axis labels
    ctx.fillStyle = colors.text;
    ctx.font = '14px Arial';
    ctx.textAlign = 'center';

    // X-axis label
    ctx.fillText('Power [W]', canvas.width / 2, canvas.height - 50);

    // Y-axis label (rotated)
    ctx.save();
    ctx.translate(20, canvas.height / 2);
    ctx.rotate(-Math.PI / 2);
    ctx.fillText('Numbers of Power Peaks', 0, 0);
    ctx.restore();

    // Draw Y-axis ticks and labels
    ctx.textAlign = 'right';
    ctx.font = '12px Arial';
    for (let i = 0; i <= 5; i++) {
      const count = (i / 5) * maxCount;
      const y = yScale(count);

      // Tick mark
      ctx.beginPath();
      ctx.moveTo(padding - 5, y);
      ctx.lineTo(padding + 5, y);
      ctx.stroke();

      // Label
      ctx.fillText(Math.round(count).toString(), padding - 10, y + 5);
    }

    // Draw X-axis ticks and labels
    ctx.textAlign = 'center';
    const numXTicks = Math.min(10, bin_centers.length);
    for (let i = 0; i <= numXTicks; i++) {
      const power = minPower + (i / numXTicks) * (maxPower - minPower);
      const x = xScale(power);

      // Tick mark
      ctx.beginPath();
      ctx.moveTo(x, padding + chartHeight - 5);
      ctx.lineTo(x, padding + chartHeight + 5);
      ctx.stroke();

      // Label
      ctx.fillText(Math.round(power).toString(), x, padding + chartHeight + 20);
    }

    // Draw histogram bars
    const barWidth = chartWidth / counts.length;

    for (let i = 0; i < counts.length; i++) {
      const count = counts[i];
      if (count === 0) continue;

      const x1 = xScale(bin_edges[i]);
      const x2 = xScale(bin_edges[i + 1]);
      const barX = x1;
      const barW = x2 - x1;
      const barH = chartHeight * (count / maxCount);
      const barY = padding + chartHeight - barH;

      // Determine bar color based on whether it's an outlier region
      let fillColor = 'rgba(66, 165, 245, 0.6)'; // Default blue
      let strokeColor = '#1976d2';

      if (statistics?.upper_bound && bin_centers[i] > statistics.upper_bound) {
        // High outlier region (red)
        fillColor = 'rgba(239, 68, 68, 0.6)';
        strokeColor = '#d32f2f';
      } else if (statistics?.Q3 && bin_centers[i] > statistics.Q3) {
        // Above Q3 but not outlier (orange)
        fillColor = 'rgba(255, 152, 0, 0.6)';
        strokeColor = '#f57c00';
      }

      // Draw bar
      ctx.fillStyle = fillColor;
      ctx.fillRect(barX, barY, barW, barH);

      ctx.strokeStyle = strokeColor;
      ctx.lineWidth = 1;
      ctx.strokeRect(barX, barY, barW, barH);
    }

    // Draw reference lines if statistics provided
    if (statistics) {
      ctx.setLineDash([5, 5]);
      ctx.lineWidth = 2;

      // Q3 line (orange)
      if (statistics.Q3) {
        const q3X = xScale(statistics.Q3);
        ctx.strokeStyle = '#f57c00';
        ctx.beginPath();
        ctx.moveTo(q3X, padding);
        ctx.lineTo(q3X, padding + chartHeight);
        ctx.stroke();

        // Label
        ctx.fillStyle = colors.accent;
        ctx.font = '11px Arial';
        ctx.textAlign = 'left';
        ctx.fillText(`Q3: ${statistics.Q3.toFixed(0)}W`, q3X + 5, padding + 20);
      }

      // Upper bound line (red)
      if (statistics.upper_bound) {
        const ubX = xScale(statistics.upper_bound);
        ctx.strokeStyle = '#d32f2f';
        ctx.beginPath();
        ctx.moveTo(ubX, padding);
        ctx.lineTo(ubX, padding + chartHeight);
        ctx.stroke();

        // Label
        ctx.fillStyle = isDark ? '#ef5350' : '#d32f2f';
        ctx.font = '11px Arial';
        ctx.textAlign = 'left';
        ctx.fillText(`Upper Bound: ${statistics.upper_bound.toFixed(0)}W`, ubX + 5, padding + 40);
      }

      ctx.setLineDash([]);
    }

    // Draw legend - horizontal unter der X-Achse
    const legendY = padding + chartHeight + 70;
    const legendItemWidth = 150;
    
    ctx.font = '12px Arial';
    ctx.textAlign = 'left';

    let legendItemX = padding;

    // Normal range
    ctx.fillStyle = colors.primaryLight;
    ctx.fillRect(legendItemX, legendY - 5, 20, 10);
    ctx.strokeStyle = '#1976d2';
    ctx.strokeRect(legendItemX, legendY - 5, 20, 10);
    ctx.fillStyle = colors.text;
    ctx.fillText('Normal Range', legendItemX + 25, legendY + 4);

    if (statistics) {
      legendItemX += legendItemWidth;

      // Above Q3
      ctx.fillStyle = colors.accentLight;
      ctx.fillRect(legendItemX, legendY - 5, 20, 10);
      ctx.strokeStyle = '#f57c00';
      ctx.strokeRect(legendItemX, legendY - 5, 20, 10);
      ctx.fillStyle = colors.text;
      ctx.fillText('Above Q3', legendItemX + 25, legendY + 4);

      legendItemX += legendItemWidth;

      // Outliers
      ctx.fillStyle = isDark ? 'rgba(239, 83, 80, 0.6)' : 'rgba(211, 47, 47, 0.6)';
      ctx.fillRect(legendItemX, legendY - 5, 20, 10);
      ctx.strokeStyle = '#d32f2f';
      ctx.strokeRect(legendItemX, legendY - 5, 20, 10);
      ctx.fillStyle = colors.text;
      ctx.fillText('Outliers', legendItemX + 25, legendY + 4);
    }

  }, [histogramData, statistics, colors, isDark]);

  useEffect(() => {
    drawChart();
  }, [drawChart]);

  if (!histogramData || !histogramData.counts || histogramData.counts.length === 0) {
    return (
      <Box sx={{ textAlign: 'center', py: 4 }}>
        <Typography variant="body2" color="text.secondary">
          Keine Histogram-Daten verfügbar
        </Typography>
      </Box>
    );
  }

  return (
    <>
      <Box sx={{ position: 'relative' }}>
        {title && (
          <Typography variant="h6" fontWeight="600" gutterBottom>
            {title}
          </Typography>
        )}
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Verteilung der Leistungsspitzen
        </Typography>
        <Box sx={{ position: 'relative', cursor: 'zoom-in' }}>
          <canvas
            ref={canvasRef}
            width={800}
            height={height}
            style={{ width: '100%', height: 'auto', maxWidth: '100%' }}
            onClick={() => setIsZoomed(true)}
          />
          <IconButton
            sx={{
              position: 'absolute',
              top: 8,
              right: 8,
              bgcolor: 'rgba(255, 255, 255, 0.8)',
              '&:hover': { bgcolor: 'rgba(255, 255, 255, 0.95)' }
            }}
            size="small"
            onClick={() => setIsZoomed(true)}
          >
            <ZoomInIcon fontSize="small" />
          </IconButton>
        </Box>
      </Box>

      {/* Zoom Modal */}
      <Modal
        open={isZoomed}
        onClose={() => setIsZoomed(false)}
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          p: 2
        }}
      >
        <Box
          sx={{
            position: 'relative',
            bgcolor: 'background.paper',
            borderRadius: 2,
            boxShadow: 24,
            p: 3,
            maxWidth: '95vw',
            maxHeight: '95vh',
            overflow: 'auto'
          }}
        >
          <IconButton
            onClick={() => setIsZoomed(false)}
            sx={{
              position: 'absolute',
              top: 8,
              right: 8,
              zIndex: 1
            }}
          >
            <CloseIcon />
          </IconButton>
          {title && (
            <Typography variant="h6" fontWeight="600" gutterBottom>
              {title}
            </Typography>
          )}
          <canvas
            width={1200}
            height={height * 1.5}
            ref={(canvas) => {
              if (canvas && isZoomed) {
                const ctx = canvas.getContext('2d');
                if (ctx && histogramData) {
                  // Redraw at larger size
                  const { counts, bin_edges, bin_centers } = histogramData;
                  const padding = 100;
                  const chartWidth = canvas.width - padding * 2;
                  const chartHeight = canvas.height - padding * 2;
                  const maxCount = Math.max(...counts);
                  const minPower = bin_edges[0];
                  const maxPower = bin_edges[bin_edges.length - 1];

                  ctx.clearRect(0, 0, canvas.width, canvas.height);
                  ctx.fillStyle = colors.background;
                  ctx.fillRect(0, 0, canvas.width, canvas.height);

                  const xScale = (power: number) => padding + ((power - minPower) / (maxPower - minPower)) * chartWidth;
                  const yScale = (count: number) => padding + chartHeight - (count / maxCount) * chartHeight;

                  // Draw axes
                  ctx.strokeStyle = colors.text;
                  ctx.lineWidth = 2;
                  ctx.beginPath();
                  ctx.moveTo(padding, padding);
                  ctx.lineTo(padding, padding + chartHeight);
                  ctx.stroke();
                  ctx.beginPath();
                  ctx.moveTo(padding, padding + chartHeight);
                  ctx.lineTo(padding + chartWidth, padding + chartHeight);
                  ctx.stroke();

                  // Draw axis labels
                  ctx.fillStyle = colors.text;
                  ctx.font = '16px Arial';
                  ctx.textAlign = 'center';
                  ctx.fillText('Power [W]', canvas.width / 2, canvas.height - 60);
                  ctx.save();
                  ctx.translate(25, canvas.height / 2);
                  ctx.rotate(-Math.PI / 2);
                  ctx.fillText('Numbers of Power Peaks', 0, 0);
                  ctx.restore();

                  // Draw Y-axis ticks
                  ctx.textAlign = 'right';
                  ctx.font = '14px Arial';
                  for (let i = 0; i <= 5; i++) {
                    const count = (i / 5) * maxCount;
                    const y = yScale(count);
                    ctx.beginPath();
                    ctx.moveTo(padding - 5, y);
                    ctx.lineTo(padding + 5, y);
                    ctx.stroke();
                    ctx.fillText(Math.round(count).toString(), padding - 10, y + 5);
                  }

                  // Draw X-axis ticks
                  ctx.textAlign = 'center';
                  const numXTicks = Math.min(10, bin_centers.length);
                  for (let i = 0; i <= numXTicks; i++) {
                    const power = minPower + (i / numXTicks) * (maxPower - minPower);
                    const x = xScale(power);
                    ctx.beginPath();
                    ctx.moveTo(x, padding + chartHeight - 5);
                    ctx.lineTo(x, padding + chartHeight + 5);
                    ctx.stroke();
                    ctx.fillText(Math.round(power).toString(), x, padding + chartHeight + 25);
                  }

                  // Draw histogram bars
                  for (let i = 0; i < counts.length; i++) {
                    const count = counts[i];
                    if (count === 0) continue;

                    const x1 = xScale(bin_edges[i]);
                    const x2 = xScale(bin_edges[i + 1]);
                    const barX = x1;
                    const barW = x2 - x1;
                    const barH = chartHeight * (count / maxCount);
                    const barY = padding + chartHeight - barH;

                    let fillColor = 'rgba(66, 165, 245, 0.6)';
                    let strokeColor = '#1976d2';

                    if (statistics?.upper_bound && bin_centers[i] > statistics.upper_bound) {
                      fillColor = 'rgba(239, 68, 68, 0.6)';
                      strokeColor = '#d32f2f';
                    } else if (statistics?.Q3 && bin_centers[i] > statistics.Q3) {
                      fillColor = 'rgba(255, 152, 0, 0.6)';
                      strokeColor = '#f57c00';
                    }

                    ctx.fillStyle = fillColor;
                    ctx.fillRect(barX, barY, barW, barH);
                    ctx.strokeStyle = strokeColor;
                    ctx.lineWidth = 1;
                    ctx.strokeRect(barX, barY, barW, barH);
                  }

                  // Draw reference lines
                  if (statistics) {
                    ctx.setLineDash([5, 5]);
                    ctx.lineWidth = 2;

                    if (statistics.Q3) {
                      const q3X = xScale(statistics.Q3);
                      ctx.strokeStyle = '#f57c00';
                      ctx.beginPath();
                      ctx.moveTo(q3X, padding);
                      ctx.lineTo(q3X, padding + chartHeight);
                      ctx.stroke();
                      ctx.fillStyle = colors.accent;
                      ctx.font = '13px Arial';
                      ctx.textAlign = 'left';
                      ctx.fillText(`Q3: ${statistics.Q3.toFixed(0)}W`, q3X + 5, padding + 25);
                    }

                    if (statistics.upper_bound) {
                      const ubX = xScale(statistics.upper_bound);
                      ctx.strokeStyle = '#d32f2f';
                      ctx.beginPath();
                      ctx.moveTo(ubX, padding);
                      ctx.lineTo(ubX, padding + chartHeight);
                      ctx.stroke();
                      ctx.fillStyle = isDark ? '#ef5350' : '#d32f2f';
                      ctx.font = '13px Arial';
                      ctx.textAlign = 'left';
                      ctx.fillText(`Upper Bound: ${statistics.upper_bound.toFixed(0)}W`, ubX + 5, padding + 50);
                    }

                    ctx.setLineDash([]);
                  }

                  // Draw legend - horizontal unter der X-Achse
                  const legendY = padding + chartHeight + 85;
                  const legendItemWidth = 180;

                  ctx.font = '14px Arial';
                  ctx.textAlign = 'left';

                  let legendItemX = padding;

                  ctx.fillStyle = colors.primaryLight;
                  ctx.fillRect(legendItemX, legendY - 5, 25, 12);
                  ctx.strokeStyle = '#1976d2';
                  ctx.strokeRect(legendItemX, legendY - 5, 25, 12);
                  ctx.fillStyle = colors.text;
                  ctx.fillText('Normal Range', legendItemX + 30, legendY + 6);

                  if (statistics) {
                    legendItemX += legendItemWidth;
                    ctx.fillStyle = colors.accentLight;
                    ctx.fillRect(legendItemX, legendY - 5, 25, 12);
                    ctx.strokeStyle = '#f57c00';
                    ctx.strokeRect(legendItemX, legendY - 5, 25, 12);
                    ctx.fillStyle = colors.text;
                    ctx.fillText('Above Q3', legendItemX + 30, legendY + 6);

                    legendItemX += legendItemWidth;
                    ctx.fillStyle = isDark ? 'rgba(239, 83, 80, 0.6)' : 'rgba(211, 47, 47, 0.6)';
                    ctx.fillRect(legendItemX, legendY - 5, 25, 12);
                    ctx.strokeStyle = '#d32f2f';
                    ctx.strokeRect(legendItemX, legendY - 5, 25, 12);
                    ctx.fillStyle = colors.text;
                    ctx.fillText('Outliers', legendItemX + 30, legendY + 6);
                  }
                }
              }
            }}
            style={{ width: '100%', height: 'auto', cursor: 'zoom-out' }}
            onClick={() => setIsZoomed(false)}
          />
        </Box>
      </Modal>
    </>
  );
};

export default PowerHistogram;
